import { useCallback } from 'react'
import { InteractionModel } from 'types/shared'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch } from 'state/store'
import { interactionModelOperations } from '../redux'
import useSaveInteraction from 'utils/useSaveInteraction'

interface UseSaveDraftInteractionParams {
  interactionModel: InteractionModel
}

const useSaveDraftInteraction = ({
  interactionModel
}: UseSaveDraftInteractionParams) => {
  const dispatch = useAppDispatch()

  const [searchParams, setSearchParams] = useSearchParams()
  const existingDraftUuid = searchParams.get('draft_interaction_uuid') || ''

  const { createInteraction, updateInteraction } = useSaveInteraction()

  const createDraftInteraction = useCallback(async () => {
    const response = await createInteraction({
      interactionModel,
      showLoading: false,
      activate: false
    })

    const draftInteractionUuid = response.data?.new_interaction_ids?.[0] || ''

    if (draftInteractionUuid) {
      setSearchParams(
        (oldSearchParams) => {
          oldSearchParams.set('draft_interaction_uuid', draftInteractionUuid)
          return oldSearchParams
        },
        { replace: true }
      )
      dispatch(interactionModelOperations.setDraftVersion('1'))
    }
  }, [createInteraction, setSearchParams, dispatch, interactionModel])

  const updateDraftInteraction = useCallback(async () => {
    await updateInteraction({
      draftUuid: existingDraftUuid,
      interactionModel,
      showLoading: false,
      activate: false
    })

    const newVersion = Number(interactionModel.draftVersion) + 1
    dispatch(interactionModelOperations.setDraftVersion(newVersion.toString()))
  }, [updateInteraction, dispatch, interactionModel, existingDraftUuid])

  return {
    createDraftInteraction,
    updateDraftInteraction
  }
}

export default useSaveDraftInteraction
